import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { elFlexJustifyCenter, elRowGap3, FlexContainer } from '@reapit/elements'
import { css, cx } from '@linaria/core'
import SideNavigations, { IRoutes } from './view/side-navigations'
import { PageLayout, CardsSection, ISections, ICardSectionViewData, IChangeRequest } from '@reapitform/dashboard-ui'
import { useDocuments } from './client-provider'
import { useNavigate, useParams } from 'react-router'
import { LastAction } from './view/last-action'
import { documentStatusIntent, documentStatusMapping } from '../../utils/documents'
import { ActivityListModal } from '../../components/modals/activity-list-modal'
import { useAgreementState } from './use-agreement-state'
import { WarningUnsavedChangesModal } from '../../components/modals/warning-unsaved-changes'
import { Badge } from '@reapitform/common-ui'

const profileMainContentCSS = css`
  max-width: calc(1200px - 22rem);
  flex: 1 1 460px;
  min-width: 460px;
  margin-right: 1.5rem;

  @media screen and (max-width: 860px) {
    width: 100%;
    margin-left: 1.5rem;
    max-width: unset;
    min-width: unset;
  }
`

const sideBarContentCss = css`
  position: sticky;
  top: 0;
  height: 100%;
  width: 20rem;
  margin-right: 2rem;
  margin-left: 1.5rem;

  @media screen and (max-width: 860px) {
    position: static;
    width: 100%;
    height: fit-content;
    margin-right: 1.5rem;
    margin-bottom: 1rem;
  }
`

const agreementListId = 'agreement-card-list'

const getRoute = (sections: ISections[] = []): IRoutes[] => {
  return sections.map((sections: ISections) => ({
    title: sections.title,
    path: sections.id,
  }))
}

const CreateForms: FC = () => {
  const [isOpenModalActivityLogModal, setIsOpenModalActivityLogModal] = useState<boolean>(false)
  const [isConfirmUnsavedChanges, setIsConfirmUnsavedChanges] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<number | string | undefined>()
  const agreementListOrbserver: MutableRefObject<IntersectionObserver | undefined> = useRef()

  const { documentId } = useParams<string>()
  const documents = useDocuments()
  const navigate = useNavigate()
  const { setSectionErrorState, setSectionChangeRequested, setIsEditing, state, setActiveMenu } = useAgreementState()

  const handleSectionLoaded = useCallback((index: number, sectionData: ICardSectionViewData[]) => {
    const errorCount = sectionData.reduce((result: number, currentSectionData: ICardSectionViewData) => {
      const currentError = currentSectionData.emptyRequiredFields ?? 0
      return result + currentError
    }, 0)

    const currentChangeRequested: IChangeRequest[] = sectionData.reduce(
      (result: IChangeRequest[], currentSectionView) => {
        const changeRequested: IChangeRequest[] | undefined = currentSectionView.changesRequested
        if (!changeRequested) return result
        return [...result, ...changeRequested]
      },
      [],
    )

    setSectionChangeRequested(index, currentChangeRequested)
    setSectionErrorState(index, errorCount)
  }, [])

  const handleChangeEditState = (isEditing: boolean, selectedId?: string | number) => {
    if (isEditing && state.isEditing && !isConfirmUnsavedChanges) {
      setSelectedId(selectedId)
      setIsConfirmUnsavedChanges(true)
      return
    }

    setIsConfirmUnsavedChanges(false)
    setIsEditing(isEditing, selectedId)

    if (!selectedId) return

    navigate(`${selectedId}`)
  }

  useEffect(() => {
    const agreementListScrollingContainer = document.getElementById(agreementListId)
    if (!agreementListScrollingContainer || !documents?.sections) return

    try {
      agreementListOrbserver.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const sectionId: string = entry.target.id
            const isActive: boolean = entry.isIntersecting

            setActiveMenu(sectionId, isActive)
          })
        },
        {
          root: agreementListScrollingContainer,
          rootMargin: '48px 0px -65% 0px',
          threshold: 1,
        },
      )
    } catch (error) {
      console.info("[Reapit Form]: Can't orbserve the current agreement cards position")
    }
  }, [])

  return (
    <PageLayout
      id={agreementListId}
      className={elFlexJustifyCenter}
      hasBodyGrayBackground
      withoutBodyContiner
      pageTitle={documents?.title}
      pageDescription={documents?.description}
      badge={
        <Badge
          key={`page-title-tag-${documents?.status}`}
          className={css`
            height: fit-content;
          `}
          intent={documentStatusIntent[documents?.status || '0']}
          isReversed
        >
          {documentStatusMapping[documents?.status || '0']}
        </Badge>
      }
    >
      <FlexContainer isFlexColumn className={cx(elRowGap3, sideBarContentCss)}>
        <SideNavigations routes={getRoute(documents?.sections)} />
        {documents?.lastActivity && (
          <LastAction
            lastActivity={documents.lastActivity}
            onViewActivity={() => setIsOpenModalActivityLogModal(true)}
          />
        )}
      </FlexContainer>
      <FlexContainer isFlexColumn className={cx(profileMainContentCSS)} tag="section">
        {documents?.sections.map((sectionItem: ISections, idx) => (
          <CardsSection
            documentId={documentId || ''}
            section={sectionItem}
            key={sectionItem.id}
            onLoaded={(data) => handleSectionLoaded(idx, data)}
            isEditing={state.editingOn === sectionItem.id}
            onEditStateChange={(isEditing, editOn) => handleChangeEditState(isEditing, editOn)}
            changeRequest={state.sectionsChangeRequested?.[idx]}
            orbserver={agreementListOrbserver.current}
          />
        ))}
      </FlexContainer>

      <WarningUnsavedChangesModal
        isOpen={isConfirmUnsavedChanges}
        onClose={() => setIsConfirmUnsavedChanges(false)}
        onContinue={() => handleChangeEditState(true, selectedId)}
      />

      {documentId && (
        <ActivityListModal
          isOpen={isOpenModalActivityLogModal}
          documentId={documentId}
          isOpenChange={(isOpen) => setIsOpenModalActivityLogModal(isOpen)}
          hideViewAgreementButton
          agreementName={documents?.headerTitle ?? '-'}
        />
      )}
    </PageLayout>
  )
}

export default CreateForms
